/**
 * `exportCustomersXlsx` — build a styled `.xlsx` from the export payload.
 *
 * Mirrors the ledger export at `apps/web/src/features/customers/ledger/`:
 *   • Info block at the top (title, active filters, generated date).
 *   • Frozen header row so long lists stay scannable.
 *   • Column widths tuned so Names, GSTIN, and dates never render as `###`.
 *   • Money columns stored as real rupee numbers with Indian lakh grouping —
 *     so Excel's SUM / SORT work.
 *   • Phone, GSTIN, and PIN written as strings with a text format so
 *     Excel doesn't auto-coerce `9820022222` into `9.82E+09`.
 *
 * ExcelJS is ~700 KB, so we `await import("exceljs")` lazily — only users
 * who actually click Export pay the download.
 */
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@flint/api/router";

/**
 * `@flint/api-contract` exports no router-output helper — its own `AppRouter`
 * mirror types every procedure as `unknown`. Derive the real shape the way
 * `lib/trpc.ts` and the style cockpit do: a type-only import from `apps/api`.
 */
type ExportPayload = inferRouterOutputs<AppRouter>["customers"]["export"];
type ExportRow = ExportPayload["rows"][number];

export interface ExportFilters {
  tab: "all" | "archived" | "overdue" | "active" | "new";
  sort: "recent" | "name" | "exposure";
  q?: string;
}

const INR_FORMAT = '"₹"#,##,##0.00;"-₹"#,##,##0.00';
const DATE_FORMAT = "yyyy-mm-dd";

const TAB_LABEL: Record<ExportFilters["tab"], string> = {
  all: "All customers",
  archived: "Archived",
  overdue: "With overdue",
  active: "Active · last 30d",
  new: "New · last 30d",
};

const SORT_LABEL: Record<ExportFilters["sort"], string> = {
  recent: "Most recently added",
  name: "Name (A → Z)",
  exposure: "Biggest exposure first",
};

const HEADERS = [
  "Name",
  "Contact",
  "GSTIN",
  "Phone",
  "Email",
  "State",
  "City",
  "PIN",
  "Payment terms",
  "Outstanding (INR)",
  "YTD invoiced (INR)",
  "Overdue invoices",
  "Total invoices",
  "Last activity",
  "Status",
  "Archived",
] as const;

const COLUMN_WIDTHS = [30, 20, 18, 15, 26, 6, 16, 8, 14, 18, 18, 10, 10, 14, 12, 14] as const;

export async function exportCustomersXlsx(
  payload: ExportPayload,
  filters: ExportFilters,
): Promise<void> {
  const ExcelJS = (await import("exceljs")).default;

  const wb = new ExcelJS.Workbook();
  wb.creator = "Flint";
  wb.created = new Date();
  const ws = wb.addWorksheet("Customers", {
    // Freeze at row 6 so the info block + header row stay visible when
    // scrolling the customer list below.
    views: [{ state: "frozen", ySplit: 6 }],
  });

  ws.columns = COLUMN_WIDTHS.map((width) => ({ width }));

  // ── Rows 1-4 — info block ──────────────────────────────────────────
  ws.mergeCells("A1:P1");
  const title = ws.getCell("A1");
  title.value = "Customers export";
  title.font = { name: "Calibri", size: 16, bold: true, color: { argb: "FF0F172A" } };
  title.alignment = { vertical: "middle", horizontal: "left" };
  ws.getRow(1).height = 28;

  const infoRows: Array<[string, string]> = [
    ["View", TAB_LABEL[filters.tab]],
    ["Sort", SORT_LABEL[filters.sort]],
    ["Search", filters.q?.trim() ? filters.q.trim() : "—"],
    ["Generated", new Date().toISOString().slice(0, 10)],
  ];
  infoRows.forEach(([label, value], i) => {
    const row = ws.getRow(2 + i);
    row.getCell(1).value = label;
    row.getCell(1).font = { bold: true, color: { argb: "FF475569" } };
    row.getCell(2).value = value;
  });
  // Also stamp row-count on the same info block (row 5, col C).
  ws.getRow(5).getCell(3).value = `${payload.rowCount} customer${payload.rowCount === 1 ? "" : "s"}${payload.truncated ? " (capped at 5000)" : ""}`;
  ws.getRow(5).getCell(3).font = { italic: true, color: { argb: "FF64748B" } };

  // ── Row 6 — data header ────────────────────────────────────────────
  // Kept simple: bold text on a light grey fill, single bottom border to
  // separate from data. No per-cell box borders — Excel's default gridlines
  // are the visual separator (like a normal CSV-opened-in-Excel).
  const headerRow = ws.getRow(6);
  HEADERS.forEach((label, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = label;
    cell.font = { bold: true, color: { argb: "FF0F172A" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF1F5F9" },
    };
    cell.alignment = { vertical: "middle", horizontal: "left" };
    cell.border = {
      bottom: { style: "thin", color: { argb: "FF94A3B8" } },
    };
  });
  headerRow.height = 20;

  // ── Rows 7+ — data ─────────────────────────────────────────────────
  payload.rows.forEach((row, i) => {
    const r = ws.getRow(7 + i);

    // Text-first fields — the numFmt "@" prevents Excel from auto-parsing
    // phone / GSTIN / PIN into scientific notation.
    r.getCell(1).value = row.name;
    r.getCell(2).value = row.contactName ?? "";
    r.getCell(3).value = row.gstin ?? "";
    r.getCell(3).numFmt = "@";
    r.getCell(4).value = row.phone ?? "";
    r.getCell(4).numFmt = "@";
    r.getCell(5).value = row.email ?? "";
    r.getCell(6).value = row.state ?? "";
    r.getCell(7).value = row.city ?? "";
    r.getCell(8).value = row.pincode ?? "";
    r.getCell(8).numFmt = "@";
    r.getCell(9).value = row.paymentTerms ?? "";

    // Money — real rupee numbers so SUM / SORT work.
    r.getCell(10).value = paiseToRupees(row.outstandingPaise);
    r.getCell(10).numFmt = INR_FORMAT;
    r.getCell(11).value = paiseToRupees(row.ytdInvoicedPaise);
    r.getCell(11).numFmt = INR_FORMAT;

    r.getCell(12).value = row.overdueInvoiceCount;
    r.getCell(13).value = row.invoiceCount;

    // Dates — real Date so Excel formats them and can sort chronologically.
    const lastAct = row.lastActivityAt ? new Date(row.lastActivityAt) : null;
    if (lastAct && !Number.isNaN(lastAct.getTime())) {
      r.getCell(14).value = lastAct;
      r.getCell(14).numFmt = DATE_FORMAT;
    } else {
      r.getCell(14).value = "";
    }

    r.getCell(15).value = statusLabel(row);

    const archivedAt = row.archivedAt ? new Date(row.archivedAt) : null;
    if (archivedAt && !Number.isNaN(archivedAt.getTime())) {
      r.getCell(16).value = archivedAt;
      r.getCell(16).numFmt = DATE_FORMAT;
    } else {
      r.getCell(16).value = "";
    }

    // No per-cell borders and no font colouring — Excel's default gridlines
    // are the visual separator. Archived rows carry their date in the
    // "Archived" column, so they're distinguishable without recolouring.
  });

  // ── Trigger download ───────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = payload.fileName;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// ── helpers ────────────────────────────────────────────────────────

function paiseToRupees(paise: string): number {
  return Number(BigInt(paise)) / 100;
}

function statusLabel(row: ExportRow): string {
  if (row.archivedAt) return "Archived";
  if (row.overdueInvoiceCount > 0) return "Overdue";
  if (BigInt(row.outstandingPaise) > 0n) return "Outstanding";
  if (row.invoiceCount > 0) return "Clear";
  return "No data";
}
